import React, { useCallback, useEffect, useMemo, useState } from 'react'
import apiClient, { API_ENDPOINTS } from '../../config/api'

const ManageExpenseCategory = () => {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [errorMessage, setErrorMessage] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true)
      setErrorMessage('')
      const response = await apiClient.get(API_ENDPOINTS.EXPENSE_CATEGORIES)
      const data = Array.isArray(response.data) ? response.data : response.data?.results || []
      setCategories(data)
    } catch (error) {
      console.error('Failed to load expense categories:', error)
      setErrorMessage(
        error.response?.data?.detail ||
          'Unable to load expense categories right now. Please try again later.'
      )
      setCategories([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  const filteredCategories = useMemo(() => {
    if (!searchTerm) {
      return categories
    }
    const query = searchTerm.toLowerCase()
    return categories.filter(
      (category) =>
        category.name?.toLowerCase().includes(query) ||
        category.description?.toLowerCase().includes(query)
    )
  }, [categories, searchTerm])

  const totalCategories = categories.length

  const renderTable = () => {
    if (loading) {
      return (
        <div className="py-5 text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )
    }

    if (!filteredCategories.length) {
      return (
        <div className="py-5 text-center text-muted">
          <i className="bi bi-tags fs-1 d-block mb-3" />
          <p className="mb-0">
            {searchTerm ? 'No categories match your search.' : 'No categories have been created yet.'}
          </p>
        </div>
      )
    }

    return (
      <div className="table-responsive">
        <table className="table table-hover align-middle">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Description</th>
              <th scope="col" className="text-end">Created</th>
            </tr>
          </thead>
          <tbody>
            {filteredCategories.map((category) => (
              <tr key={category.id}>
                <td className="fw-semibold">{category.name}</td>
                <td className="text-muted">{category.description || '—'}</td>
                <td className="text-end text-muted">
                  {category.created_at ? new Date(category.created_at).toLocaleDateString() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Manage Expense Categories</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="#">Home</a></li>
            <li className="breadcrumb-item">Expenses</li>
            <li className="breadcrumb-item active">Manage Expense Categories</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-12">
            {errorMessage && (
              <div className="alert alert-danger" role="alert">
                {errorMessage}
              </div>
            )}

            <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 mb-3">
              <div
                className="p-3 rounded-3 text-white"
                style={{
                  background: 'linear-gradient(135deg, #00c6ff 0%, #0072ff 100%)',
                  boxShadow: '0 6px 18px rgba(0, 114, 255, 0.25)'
                }}
              >
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: 48, height: 48, backgroundColor: 'rgba(255,255,255,0.2)' }}
                  >
                    <i className="bi bi-tags fs-4" />
                  </div>
                  <div>
                    <small className="text-uppercase fw-semibold text-white-50">Total Categories</small>
                    <h3 className="mb-0">{totalCategories}</h3>
                  </div>
                </div>
              </div>
              <div className="d-flex gap-2 flex-wrap">
                <input
                  type="search"
                  className="form-control"
                  placeholder="Search categories"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  style={{ minWidth: 220 }}
                />
                <button type="button" className="btn btn-outline-primary" onClick={fetchCategories}>
                  <i className="bi bi-arrow-repeat me-1" />
                  Refresh
                </button>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="d-flex justify-content-between flex-wrap align-items-center gap-2 mb-3">
                  <div>
                    <h5 className="card-title mb-0">Expense Categories</h5>
                    <small className="text-muted">Categories are shared across all expense forms.</small>
                  </div>
                  <small className="text-muted">
                    Use the <strong>Add Category</strong> screen to create new entries.
                  </small>
                </div>
                {renderTable()}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default ManageExpenseCategory
